import { useState, useRef, useLayoutEffect } from "react";
import { useReveal } from "@/components/Reveal";

/* The Complete Ecosystem — Figma node 102:3372 (Frame 159), 1654 × 982.

   Overlapping circles on a scaled stage: the composition only reads correctly
   when they keep their exact relationships, so nothing here is fluid. Below lg
   the cluster is replaced by a responsive grid. */

const STAGE_W = 1654;
const STAGE_H = 982;

const PHOTOS = [
  { key: "e1", src: "/images/eco-1.jpg", left: 18, top: 169, size: 276 },
  { key: "e2", src: "/images/eco-2.jpg", left: 305, top: 296, size: 194 },
  { key: "e3", src: "/images/eco-3.jpg", left: 650, top: 229, size: 366 },
  { key: "e10", src: "/images/eco-10.jpg", left: 521, top: 692, size: 290 },
  { key: "e11", src: "/images/eco-11.jpg", left: 1072, top: 752, size: 167 },
  { key: "e7", src: "/images/eco-7.jpg", left: 0, top: 466, size: 443 },
  { key: "e9", src: "/images/eco-9.jpg", left: 1258, top: 584, size: 396 },
  { key: "e5", src: "/images/eco-5.jpg", left: 1011, top: 349, size: 339 },
];

const BADGES = [
  { key: "b4", left: 443, top: 445, size: 247, bg: "var(--lime)", ink: "#000", fs: 32, lh: 36, w: 148, text: "500+ Creators Online" },
  { key: "b8", left: 811, top: 595, size: 261, bg: "#6BDDD5", ink: "#082523", fs: 36, lh: 38, w: 174, text: "Become Industry Ready" },
  { key: "b6", left: 1350, top: 296, size: 243, bg: "#F09595", ink: "#000", fs: 36, lh: 38, w: 174, text: "Join Now" },
];

/* Stacked layout: the same circles as three mixed rows. Widths are percentages of
   the row so the mosaic holds its proportions; `dy` staggers each off its
   neighbours' baseline. */
const MOBILE_ROWS = [
  [
    { key: "e1", w: "28%", dy: 12 },
    { key: "b4", w: "42%", dy: 0 },
    { key: "e2", w: "22%", dy: 20 },
  ],
  [
    { key: "b8", w: "40%", dy: 0 },
    { key: "e3", w: "30%", dy: 16 },
    { key: "e5", w: "22%", dy: 2 },
  ],
  [
    { key: "e7", w: "26%", dy: 14 },
    { key: "e9", w: "24%", dy: 0 },
    { key: "b6", w: "40%", dy: 10 },
  ],
];

const BY_KEY = Object.fromEntries([...PHOTOS, ...BADGES].map((c) => [c.key, c]));

export default function EcosystemSection() {
  const stageRef = useRef(null);
  const [sectionRef, inView] = useReveal();
  const [scale, setScale] = useState(0);

  const enter = (kind) => (inView ? kind : "opacity-0");

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="overflow-hidden pb-6 pt-12 lg:pb-0 lg:pt-8 xl:pt-10">
      <h2 className={`${enter("anim-rise")} mx-auto max-w-[880px] px-5 text-center font-poppins text-[34px] leading-[1.16] text-black sm:text-[46px] lg:text-[64px] lg:leading-[74px]`}>
        The Complete Ecosystem for Modern Creators.
      </h2>

      {/* The cluster frame is 1654 wide inside a 1426 page, so it is meant to bleed
          past both edges and be clipped. Rendering it at 1654/1426 of the page,
          centred, keeps the circles at their designed proportion. */}
      <div
        ref={stageRef}
        className="relative mt-10 hidden lg:block"
        style={{
          width: `${(STAGE_W / 1426) * 100}%`,
          marginLeft: `${((1426 - STAGE_W) / 1426 / 2) * 100}%`,
          height: scale ? (STAGE_H - 148) * scale : undefined,
        }}
      >
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: STAGE_W,
            height: STAGE_H - 148,
            transform: `scale(${scale || 1})`,
            visibility: scale ? "visible" : "hidden",
          }}
        >
          {PHOTOS.map((c, i) => (
            <div
              key={c.key}
              className={`${enter("anim-pop")} absolute overflow-hidden rounded-full`}
              style={{ left: c.left, top: c.top - 148, width: c.size, height: c.size, animationDelay: `${i * 0.06}s` }}
              aria-hidden
            >
              <img src={c.src} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          ))}

          {/* Delayed past the photo stagger so the message circles land last. */}
          {BADGES.map((b, i) => (
            <div
              key={b.key}
              className={`${enter("anim-pop")} absolute flex items-center justify-center rounded-full text-center`}
              style={{ left: b.left, top: b.top - 148, width: b.size, height: b.size, background: b.bg, animationDelay: `${0.5 + i * 0.08}s` }}
            >
              <span
                className="font-montserrat font-semibold"
                style={{ color: b.ink, fontSize: b.fs, lineHeight: `${b.lh}px`, width: b.w }}
              >
                {b.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── below lg: staggered mosaic ── */}
      <div className="mt-10 px-5 lg:hidden">
        <div className="mx-auto flex max-w-[560px] flex-col">
          {MOBILE_ROWS.map((row, ri) => (
            <div
              key={ri}
              className={`flex items-start justify-center gap-2 sm:gap-3 ${ri ? "-mt-3 sm:-mt-6" : ""}`}
            >
              {row.map((slot, ci) => {
                const c = BY_KEY[slot.key];
                const delay = `${(ri * 3 + ci) * 0.07}s`;
                return c.src ? (
                  <div
                    key={c.key}
                    className={`${enter("anim-pop")} aspect-square shrink-0 overflow-hidden rounded-full`}
                    style={{ width: slot.w, marginTop: slot.dy, animationDelay: delay }}
                    aria-hidden
                  >
                    <img src={c.src} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div
                    key={c.key}
                    className={`${enter("anim-pop")} flex aspect-square shrink-0 items-center justify-center rounded-full px-3 text-center`}
                    style={{ width: slot.w, marginTop: slot.dy, background: c.bg, animationDelay: delay }}
                  >
                    <span
                      className="font-montserrat text-[13px] font-semibold leading-[1.15] sm:text-[20px]"
                      style={{ color: c.ink }}
                    >
                      {c.text}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
